import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { ArrowLeft, CalendarDays, Download, Upload, Trash2, FileText } from "lucide-react";

export async function downloadSecureFile(path, filename) {
  const res = await fetch(`${API}/files/download?path=${encodeURIComponent(path)}`, { credentials: "include" });
  if (!res.ok) throw new Error("Téléchargement impossible");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function TimetableRow({ row, busy, onDownload, onUpload, onDelete }) {
  const inputRef = useRef(null);
  const tt = row.timetable;
  return (
    <tr className="border-b border-[var(--parchment-2)]/60" data-testid={`timetable-row-${row.teacher.id}`}>
      <td className="py-3 font-medium">{row.teacher.name}</td>
      <td className="py-3 text-xs text-[var(--ink-soft)]">{row.teacher.email}</td>
      <td className="py-3">
        {tt ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-[var(--ink)]">
            <FileText size={14} className="text-[var(--maroon)]" /> {tt.name}
          </span>
        ) : (
          <span className="text-xs italic text-[var(--muted)]">Aucun emploi du temps</span>
        )}
      </td>
      <td className="py-3">
        <div className="flex items-center gap-2 justify-end">
          {tt && (
            <button
              className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5"
              onClick={() => onDownload(tt)}
              data-testid={`download-timetable-${row.teacher.id}`}
            >
              <Download size={13} /> Télécharger
            </button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(row.teacher, f);
              e.target.value = "";
            }}
            data-testid={`upload-timetable-input-${row.teacher.id}`}
          />
          <button
            className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            data-testid={`upload-timetable-btn-${row.teacher.id}`}
          >
            <Upload size={13} /> {tt ? "Remplacer" : "Ajouter"}
          </button>
          {tt && (
            <button
              className="btn-secondary !py-1.5 !px-2 text-xs !text-[var(--late)]"
              disabled={busy}
              onClick={() => onDelete(row.teacher)}
              title="Supprimer l'emploi du temps"
              data-testid={`delete-timetable-${row.teacher.id}`}
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function Timetables() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/timetables");
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const download = async (tt) => {
    try {
      await downloadSecureFile(tt.path, tt.name);
    } catch {
      toast.error("Échec du téléchargement");
    }
  };

  const upload = async (teacher, file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/teachers/${teacher.id}/timetable`, { path: data.path, name: file.name });
      toast.success(`Emploi du temps de ${teacher.name} enregistré`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (teacher) => {
    setBusy(true);
    try {
      await api.delete(`/teachers/${teacher.id}/timetable`);
      toast.success("Emploi du temps supprimé");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const backTo = user?.role === "direction" ? "/admin" : "/teacher";

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />
      <Toaster richColors position="top-right" />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <Link to={backTo} className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="timetables-back">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        <div className="mt-6 pb-8 border-b border-[var(--parchment-2)]">
          <div className="eyebrow flex items-center gap-2"><CalendarDays size={14} /> Emplois du temps · 2026-2027</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2">Emplois du temps des professeurs</h1>
          <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
            Consultez, ajoutez ou remplacez l'emploi du temps (PDF) de chaque professeur.
            Chaque professeur voit le sien dans son espace.
          </p>
        </div>

        <div className="mt-8 bg-white border border-[var(--parchment-2)] p-6 overflow-x-auto">
          {!rows ? (
            <div className="text-[var(--ink-soft)]">Chargement...</div>
          ) : (
            <table className="w-full text-sm" data-testid="timetables-table">
              <thead>
                <tr className="border-b border-[var(--parchment-2)] text-left">
                  <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Professeur</th>
                  <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Email</th>
                  <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Fichier</th>
                  <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <TimetableRow key={r.teacher.id} row={r} busy={busy} onDownload={download} onUpload={upload} onDelete={remove} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
